
import { useCallback, useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { acquireApiAccessTokenAsync } from '../../../services/auth/AuthService';
import { useProviderServices } from '../../../app/providers/AppServicesContext';
import {
    setBannerConfig,
    setShowNotificationBannerDialog,
} from '../../../redux/features/notificationBanner/notificationBannerSlice';
import deriveNotificationBannerState from '../helpers/deriveNotificationBannerState';
import { shallowEqual } from 'react-redux';

// Handles notification banner and dialog behavior
// - retrieves banner config from backend and stores in redux
// - determines when to show banner based on config and user interaction (local state)


export interface NotificationBannerVm {
    shouldShowBanner: boolean;
    closeBanner: () => void;
}

export const useNotificationBanner = (): NotificationBannerVm => {
    const dispatch = useAppDispatch();
    const activeUserInfo = useAppSelector((state) => state.app.activeUserInfo);
    const { instance } = useMsal();
    const providerServices = useProviderServices();

    // local state for user interaction
    const [isBannerDismissed, setIsBannerDismissed] = useState(false);

    // derive banner visibility and dialog auto-open from config and user interaction
    const { shouldShowBanner, shouldAutoOpenDialog } = useAppSelector((state) =>
        deriveNotificationBannerState(state, { isBannerDismissed }),
        shallowEqual
    );

    useEffect(() => {
        const loadConfigAsync = async () => {

            try {
                const accessToken = await acquireApiAccessTokenAsync({
                    msalInstance: instance,
                });

                const config = await providerServices.proGPT.settings.getAnnouncementConfigAsync(accessToken);
                dispatch(setBannerConfig(config));
            } catch (error) {
                console.error('Failed to load notification banner config:', error);
                dispatch(setBannerConfig({}));
            }

            setIsBannerDismissed(false);
        };

        void loadConfigAsync();
    }, [activeUserInfo, dispatch, instance, providerServices]);

    useEffect(() => {
        if (shouldAutoOpenDialog) {
            dispatch(setShowNotificationBannerDialog(true));
        }
    }, [dispatch, shouldAutoOpenDialog]);

    const closeBanner = useCallback(() => {
        setIsBannerDismissed(true);
        dispatch(setShowNotificationBannerDialog(false));
    }, [dispatch]);

    return {
        shouldShowBanner,
        closeBanner,
    };
};

export default useNotificationBanner;